import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SettingsProvider, useSettings } from "@/contexts/SettingsContext";
import { canAccessRoute } from "@/lib/permissions";
import { Toaster } from "sonner";
import AppLayout from "@/components/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import PipelinePage from "@/pages/PipelinePage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import FollowUpsPage from "@/pages/FollowUpsPage";
import SettingsPage from "@/pages/SettingsPage";
import TeamPage from "@/pages/TeamPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AutomationsPage from "@/pages/AutomationsPage";
import ReportsPage from "@/pages/ReportsPage";
import CalendarPage from "@/pages/CalendarPage";
import CaptureFormPage from "@/pages/CaptureFormPage";
import WorkshopBuilderPage from "@/pages/WorkshopBuilderPage";
import ScrumBuilderPage from "@/pages/ScrumBuilderPage";
import BeweegaanbodBuilderPage from "@/pages/BeweegaanbodBuilderPage";
import PakketBuilderPage from "@/pages/PakketBuilderPage";
import WorkshopTemplatesPage from "@/pages/WorkshopTemplatesPage";
import BuilderHubPage from "@/pages/BuilderHubPage";
import ContentPlannerPage from "@/pages/ContentPlannerPage";
import MeetingTimerPage from "@/pages/MeetingTimerPage";
import EbookLibraryPage from "@/pages/EbookLibraryPage";
import ToolHubPage from "@/pages/ToolHubPage";
import VitaliteitBenchmarkPage from "@/pages/VitaliteitBenchmarkPage";
import ArbeidsvoorwaardenCheckPage from "@/pages/ArbeidsvoorwaardenCheckPage";
import PomodoroTimerPage from "@/pages/PomodoroTimerPage";
import PublicFormPage from "@/pages/PublicFormPage";

function ProtectedRoute({ children }) {
  const { user, loading, userPermissions, isAdminPermissions } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!canAccessRoute(user.role, location.pathname, userPermissions, isAdminPermissions)) {
    return <Navigate to="/calendar" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

function SettingsRefresher() {
  const location = useLocation();
  const { onNavigate } = useSettings();
  useEffect(() => { onNavigate(); }, [location.pathname, onNavigate]);
  return null;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <SettingsRefresher />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/capture" element={<CaptureFormPage />} />
        <Route path="/workshop-builder" element={<WorkshopBuilderPage />} />
        <Route path="/scrum-builder" element={<ScrumBuilderPage />} />
        <Route path="/beweegaanbod-builder" element={<BeweegaanbodBuilderPage />} />
        <Route path="/pakket-builder" element={<PakketBuilderPage />} />
        <Route path="/meeting-timer" element={<MeetingTimerPage />} />
        <Route path="/ebook-library" element={<EbookLibraryPage />} />
        <Route path="/vitaliteit-benchmark" element={<VitaliteitBenchmarkPage />} />
        <Route path="/arbeidsvoorwaarden-check" element={<ArbeidsvoorwaardenCheckPage />} />
        <Route path="/pomodoro-timer" element={<PomodoroTimerPage />} />
        <Route path="/forms/:slug" element={<PublicFormPage />} />
        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
        <Route path="/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
        <Route path="/pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
        <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
        <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
        <Route path="/workshop-templates" element={<ProtectedRoute><WorkshopTemplatesPage /></ProtectedRoute>} />
        <Route path="/builders" element={<ProtectedRoute><BuilderHubPage /></ProtectedRoute>} />
        <Route path="/tool-hub" element={<ProtectedRoute><ToolHubPage /></ProtectedRoute>} />
        <Route path="/content-planner" element={<ProtectedRoute><ContentPlannerPage /></ProtectedRoute>} />
        <Route path="/activities" element={<ProtectedRoute><ActivitiesPage /></ProtectedRoute>} />
        <Route path="/follow-ups" element={<ProtectedRoute><FollowUpsPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
        <Route path="/automations" element={<ProtectedRoute><AutomationsPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
